import { Injectable } from '@angular/core';
import { MessageDialogActionColor, MessageDialogComponent, MessageDialogData } from 'src/app/components/dialogs/message-dialog/message-dialog.component';
import { CloudFavoriteFolders } from 'src/app/core/interfaces';
import { ToastrActionEnum, ToastrStringsEnum } from 'src/app/utils/enums';
import { SettingsService } from '../settings/settings.service';
import { ToastrDialogService } from '../toastr-dialog.service';
import { CloudServiceEnum } from "./enums";
import { MatDialog } from '@angular/material/dialog';
import { CloudData } from './metadata/CloudData';
import { GapiSession } from './google-drive/gapi.session';
import { OneDriveAuthService } from './one-drive/one-drive.auth.service';
import { DropboxAuthService } from './dropbox/dropbox-auth.service';

@Injectable({
  providedIn: 'root'
})
export class CloudFavoriteFilesService {
  private cloudAccountId;

  constructor(private readonly settingsService: SettingsService,
    private readonly toastrService: ToastrDialogService,
    private readonly dialog: MatDialog,
    private readonly gapiService: GapiSession,
    private readonly oneDriveService: OneDriveAuthService,
    private readonly dropBoxService: DropboxAuthService
  ) { }

  async setCloudAccountIdOnStart(cloudService: CloudServiceEnum) {
    this.cloudAccountId = await this.getCloudAccount(cloudService);
  }

  async getFiles(cloudService: CloudServiceEnum): Promise<CloudData[]> {
    this.cloudAccountId = await this.getCloudAccount(cloudService);
    if (!this.cloudAccountId) {
      return [] as CloudData[];
    }

    const cloudFavorites = this.settingsService.settings.cloudFavoriteFolders.find(f => f.cloud === cloudService && f.accountId === this.cloudAccountId)
    if (cloudFavorites !== undefined && cloudFavorites.folders !== undefined) {
      return cloudFavorites.folders;
    }

    return [] as CloudData[];
  }

  async toggleFavoriteFolder(folder: CloudData | null, cloudService: CloudServiceEnum) {
    if (folder !== null) {
      this.cloudAccountId = await this.getCloudAccount(cloudService);
      if (!this.cloudAccountId) {
        return;
      }

      const settings = this.settingsService.settings;
      let favoriteIndex = settings.cloudFavoriteFolders.findIndex(f => f.cloud === cloudService && f.accountId === this.cloudAccountId);

      // If the cloud is not found for this user..
      if (!(favoriteIndex >= 0)) {
        // Create
        const cloudFavoriteFolder: CloudFavoriteFolders = {
          cloud: cloudService,
          accountId: this.cloudAccountId,
          folders: []
        }
        settings.cloudFavoriteFolders.push(cloudFavoriteFolder)
        favoriteIndex = settings.cloudFavoriteFolders.findIndex(f => f.cloud === cloudService && f.accountId === this.cloudAccountId);
      }

      const folderIndex = settings.cloudFavoriteFolders[favoriteIndex].folders.findIndex(f => f.id === folder.id);
      if (folderIndex >= 0) {
        // Remove folder from favorites
        this.removeFolderFromFavorite(favoriteIndex, folderIndex);
      } else {
        // Add folder to favorites
        settings.cloudFavoriteFolders[favoriteIndex].folders.push(folder);
        this.toastrService.showToastrByType(ToastrActionEnum.Success, ToastrStringsEnum.FavoriteAdd, false);
        this.settingsService.saveSettings(settings);
      }
    }
  }

  private async removeFolderFromFavorite(favoriteIndex: number, folderIndex: number) {
    const settings = this.settingsService.settings;

    await this.showRemoveWarning();

    settings.cloudFavoriteFolders[favoriteIndex].folders.splice(folderIndex, 1);
    this.toastrService.showToastrByType(ToastrActionEnum.Success, ToastrStringsEnum.FavoriteRemove, false);
    this.settingsService.saveSettings(settings);
  }

  private async showRemoveWarning(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const data: MessageDialogData = {
        title: 'FAVORITES.REMOVE_WARNING.TITLE',
        message: 'FAVORITES.REMOVE_WARNING.MESSAGE',
        actions: [
          {
            title: 'FAVORITES.REMOVE_WARNING.ACTIONS.REMOVE',
            color: MessageDialogActionColor.Primary,
            handler: () => resolve()
          },
          {
            title: 'FAVORITES.REMOVE_WARNING.ACTIONS.CANCEL',
            color: MessageDialogActionColor.None,
            handler: () => reject('canceled')
          }
        ]
      };
      this.dialog.open(MessageDialogComponent, { data });
    });
  }

  isFavorite(item: CloudData, cloudService: CloudServiceEnum): boolean {
    if (item !== null) {
      const settings = this.settingsService.settings;
      let favoriteIndex = settings.cloudFavoriteFolders.findIndex(f => f.cloud === cloudService && f.accountId === this.cloudAccountId);

      if (!(favoriteIndex >= 0)) {
        return false;
      }

      const folderIndex = settings.cloudFavoriteFolders[favoriteIndex].folders.findIndex(f => f.id === item.id);
      return folderIndex >= 0;
    }

    return false;
  }

  private async getCloudAccount(cloudService: CloudServiceEnum): Promise<string | null> {
    switch (cloudService) {
      case CloudServiceEnum.DropBox:
        const dbxProfile = await this.dropBoxService.getUserInfo();
        return dbxProfile?.account_id || null;
      case CloudServiceEnum.GoogleDrive:
        const gdriveProfile = await this.gapiService.getUserInfo();
        return gdriveProfile;
      case CloudServiceEnum.OneDrive:
        const oneDriveProfile = await this.oneDriveService.getUserInfo();
        return oneDriveProfile?.id || null;
      default: return null;
    }
  }
}
